import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { OverlayPanel } from 'primereact/overlaypanel';
import {
  Row,
  Col,
  Button,
  Form,
  InputGroup,
  FormGroup,
  Label,
  Input,
  Card,
  CardBody,
  CardHeader,
} from 'reactstrap';
import { useState, useEffect, useContext } from 'react';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { Select } from '../../pages/AdminStoreDetail/Menu/ProductItem/ProductOrderSheet/AddExtraGroupModal/style';
import i18n from '@/utils/i18n';
import { useHistory, useLocation } from 'react-router-dom';
import { FaQrcode } from 'react-icons/fa';
import { OverlayPopover } from '../../components/OverlayPopover';
import { DateTimeHelpers } from '../../tools/DateTimeHelpers';
import { getContainerQrCodeImgUrl } from '@/utils/qrCode';
import { ContainerLocationStatus } from '@/utils/containerLocation';
import { Communicator } from '../../tools/Communication';
import StoreSearch from '../../components/StoreSearch';
import { Environment, getEnvironment } from '@/utils/env';
import { LayoutStoreInstance } from '../../LayoutStore';
import { ContainerType } from '../Events/OrderFulfillment/Components/OrderFulfillmentAddContainerTypes';
import ContainerTypeSelector from '../../components/ContainerType/ContainerTypeSelector';
import {
  CanAccessUserData,
  ViewerContext,
} from '../../providers/ViewerContextProvider';
import { Routes } from '@/utils/routes';
import { Link } from 'react-router-dom';

interface ContainerLocation {
  id: number;
  ownerId: string;
  ownerName: string | null;
  nextOwnerId: string | null;
  containerId: string;
  containerShortId: string | null;
  containerName: string | null;
  containerTypeId: number;
  ownerSince: string;
  ownerTill: string | null;
  status: ContainerLocationStatus;
}

interface ContainerSearchResult {
  offset: number;
  totalCount: number;
  locations: ContainerLocation[];
}

interface ResultResponse {
  result: string | null;
}

interface ContainerListViewProps {}

const ContainerListView: React.FC<ContainerListViewProps> = () => {
  const history = useHistory();
  const location = useLocation();
  const viewer = useContext(ViewerContext);

  const urlParams = new URLSearchParams(location.search);
  const preselectedContainerIdsUrlString =
    urlParams.get('preselectedContainerIds') ||
    encodeURIComponent(JSON.stringify([]));

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [locations, setLocations] = useState<ContainerLocation[]>([]);
  const [selection, setSelection] = useState<ContainerLocation[]>([]);
  const [containerTypes, setContainerTypes] = useState<ContainerType[]>([]);
  const [containerTypesById, setContainerTypesById] = useState<
    Record<number, string>
  >({});
  const [selectedStore, setSelectedStore] = useState<{
    uuid: string;
    name: string;
  } | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [listOffset, setListOffset] = useState(0);
  const [listLimit, setListLimit] = useState(100);
  const [searchOnlyCurrentLocation, setSearchOnlyCurrentLocation] = useState(
    urlParams.get('isCurrentLocation')
      ? urlParams.get('isCurrentLocation') === 'true'
      : true,
  );
  const [searchContainerName, setSearchContainerName] = useState(
    urlParams.get('name') || '',
  );
  const [searchContainerNameExactMatch, setSearchContainerNameExactMatch] =
    useState(urlParams.get('exactMatch') === 'true');
  const [searchContainerNameEnabled, setSearchContainerNameEnabled] = useState(
    urlParams.get('searchName') === 'true',
  );
  const [searchContainerTypeId, setSearchContainerTypeId] = useState(
    parseInt(urlParams.get('typeId') || '1'),
  );
  const [searchContainerTypeIdEnabled, setSearchContainerTypeIdEnabled] =
    useState(urlParams.get('searchTypeId') === 'true');
  const [searchContainerOwnerId, setSearchContainerOwnerId] = useState(
    urlParams.get('ownerId') || '',
  );
  const [searchContainerOwnerIdEnabled, setSearchContainerOwnerIdEnabled] =
    useState(urlParams.get('searchOwnerId') === 'true');
  const [searchContainerPreviousOwnerId, setSearchContainerPreviousOwnerId] =
    useState(urlParams.get('previousOwnerId') || '');
  const [
    searchContainerPreviousOwnerIdEnabled,
    setSearchContainerPreviousOwnerIdEnabled,
  ] = useState(urlParams.get('searchPreviousOwnerId') === 'true');
  const [searchTapToBorrowTransactionId, setSearchTapToBorrowTransactionId] =
    useState(urlParams.get('tapToBorrowTransactionId') || '');
  const [
    searchTapToBorrowTransactionIdEnabled,
    setSearchTapToBorrowTransactionIdEnabled,
  ] = useState(urlParams.get('searchTapToBorrowTransactionId') === 'true');
  const [searchContainerStoreIdEnabled, setSearchContainerStoreIdEnabled] =
    useState(urlParams.get('searchStoreId') === 'true');
  const [selectedStoreId, setSelectedStoreId] = useState(
    urlParams.get('storeId') || '',
  );
  const [
    searchContainerLocationStatusEnabled,
    setSearchContainerLocationStatusEnabled,
  ] = useState(urlParams.get('searchStatusId') === 'true');
  const [searchContainerLocationStatusId, setSearchContainerLocationStatusId] =
    useState(ContainerLocationStatus.Lost);
  const [preselectedContainerIds, setPreselectedContainerIds] = useState<
    string[]
  >(JSON.parse(decodeURIComponent(preselectedContainerIdsUrlString)));
  const [goToContainerByIdOrUrl, setGoToContainerByIdOrUrl] = useState('');
  const [showChangeTypeDialog, setShowChangeTypeDialog] = useState(false);
  const [changeTypeToTypeId, setChangeTypeToTypeId] = useState(1);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferTargetUserId, setTransferTargetUserId] = useState<
    string | null
  >(null);
  const [showDeadlineExtendDialog, setShowDeadlineExtendDialog] =
    useState(false);
  const [newMinDeadline, setNewMinDeadline] = useState<string | undefined>();
  const [showTransferToStoreDialog, setShowTransferToStoreDialog] =
    useState(false);
  const [transferToStoreSelectedStore, setTransferToStoreSelectedStore] =
    useState<{ uuid: string; name: string } | null>(null);
  const [
    showChangeContainerLocationStatusDialog,
    setShowChangeContainerLocationStatusDialog,
  ] = useState(false);
  const [changeStatusToStatusId, setChangeStatusToStatusId] = useState(
    ContainerLocationStatus.Lost,
  );

  const goToContainer = () => {
    if (goToContainerByIdOrUrl == null || goToContainerByIdOrUrl.length === 0) {
      return;
    }

    let code = goToContainerByIdOrUrl;

    if (code.length === 6) {
      code = `HTTP://VYT.TO/${code}`;
    }

    Communicator.Get('api/3/Container/CheckCode', {
      code: code,
    }).then((response) => {
      if (response.status !== 200) {
        alert(i18n.t('containerSearch:errorResolvingContainer'));
      } else {
        response.json().then((data: any) => {
          if (data.type !== 'Container') {
            alert(i18n.t('containerSearch:thisIsNotAContainer'));
          } else if (!data.codeOk) {
            alert(i18n.t('containerSearch:thisIsNotAValidContainerCode'));
          } else {
            history.push(Routes.container(data.id));
          }
        });
      }
    });
  };

  const updateContainerTypes = () => {
    Communicator.Get('container/GetAllContainerCategories', {})
      .then((response) => response.json())
      .then((data: { containerTypes: ContainerType[] }) => {
        const types = data.containerTypes;
        let typeDict: Record<number, string> = {};

        for (let i = 0; i < types.length; i++) {
          const type = types[i];
          typeDict[type.id] = type.name;
        }

        setContainerTypesById(typeDict);
        setContainerTypes(data.containerTypes);
      });
  };

  const updateData = (
    listOffsetParam: number | null = null,
    initialLoad = false,
  ) => {
    let newListOffset = listOffsetParam === null ? listOffset : listOffsetParam;
    if (listOffsetParam !== null) {
      setListOffset(listOffsetParam);
    }

    let constraints = [];

    if (searchOnlyCurrentLocation) {
      constraints.push({
        type: 'ContainerConstraintIsCurrentLocation',
        data: JSON.stringify({ isCurrentLocation: true }),
      });
    }
    if (searchContainerNameEnabled) {
      const data = JSON.stringify({
        name: searchContainerName,
        exactMatch: searchContainerNameExactMatch,
      });
      constraints.push({ type: 'ContainerConstraintName', data });
    }
    if (searchContainerTypeIdEnabled && searchContainerTypeId !== null) {
      constraints.push({
        type: 'ContainerConstraintTypeId',
        data: JSON.stringify({ typeId: searchContainerTypeId }),
      });
    }
    if (searchContainerOwnerIdEnabled) {
      constraints.push({
        type: 'ContainerConstraintOwnerId',
        data: JSON.stringify({ ownerId: searchContainerOwnerId }),
      });
    }
    if (searchContainerPreviousOwnerIdEnabled) {
      constraints.push({
        type: 'ContainerConstraintPreviousOwnerId',
        data: JSON.stringify({
          previousOwnerId: searchContainerPreviousOwnerId,
        }),
      });
    }
    if (searchContainerStoreIdEnabled) {
      constraints.push({
        type: 'ContainerConstraintStoreId',
        data: JSON.stringify({ storeId: selectedStoreId }),
      });
    }
    if (searchContainerLocationStatusEnabled) {
      constraints.push({
        type: 'ContainerConstraintContainerLocationStatus',
        data: JSON.stringify({
          statuses: [searchContainerLocationStatusId],
        }),
      });
    }
    if (preselectedContainerIds.length > 0) {
      constraints.push({
        type: 'ContainerConstraintContainerId',
        data: JSON.stringify({ ids: preselectedContainerIds }),
      });
    }
    if (
      searchTapToBorrowTransactionIdEnabled &&
      searchTapToBorrowTransactionId
    ) {
      constraints.push({
        type: 'ContainerConstraintTapToBorrowTransactionId',
        data: JSON.stringify({
          tapToBorrowTransactionId: searchTapToBorrowTransactionId,
        }),
      });
    }

    console.log(constraints);

    setIsLoading(true);

    let paramsObj = {
      searchStoreId: searchContainerStoreIdEnabled.toString(),
      storeId: selectedStoreId.toString(),
      isCurrentLocation: searchOnlyCurrentLocation.toString(),
      name: searchContainerName.toString(),
      exactMatch: searchContainerNameExactMatch.toString(),
      searchName: searchContainerNameEnabled.toString(),
      typeId: searchContainerTypeId.toString(),
      searchTypeId: searchContainerTypeIdEnabled.toString(),
      ownerId: searchContainerOwnerId.toString(),
      searchOwnerId: searchContainerOwnerIdEnabled.toString(),
      previousOwnerId: searchContainerPreviousOwnerId.toString(),
      searchPreviousOwnerId: searchContainerPreviousOwnerIdEnabled.toString(),
      searchStatusId: searchContainerLocationStatusEnabled.toString(),
      statusIds: searchContainerLocationStatusId.toString(),
      searchTapToBorrowTransactionId:
        searchTapToBorrowTransactionIdEnabled.toString(),
      tapToBorrowTransactionId: searchTapToBorrowTransactionId.toString(),
    };

    const params = new URLSearchParams(paramsObj).toString();
    history.push(`${Routes.containers()}?${params}`);

    // only load container locations on mount when some constraints where preselected via url
    if (!initialLoad || constraints.length !== 1) {
      Communicator.PostData('container/SearchContainers', {
        offset: newListOffset,
        limit: listLimit,
        constraints: constraints,
      })
        .then((response) => response.json())
        .then((data: ContainerSearchResult) => {
          setTotalCount(data.totalCount);
          setLocations(data.locations);
          setSelection([]);
          setIsLoading(false);

          if (preselectedContainerIds.length > 0) {
            setSelection(data.locations);
            setPreselectedContainerIds([]);
          }
        });
    } else {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    updateContainerTypes();
    updateData(0, true);
  }, []);

  const renderContainerCell = (location: ContainerLocation) => {
    return (
      <span>
        <Link to={Routes.container(location.containerId)}>
          {location.containerName}
        </Link>{' '}
        <OverlayPopover
          body={
            <img
              alt="QR code for container"
              src={getContainerQrCodeImgUrl(
                location.containerId,
                location.containerShortId,
              )}
            />
          }
          header={i18n.t('Containers:qrCodeFor', {
            containerName: location.containerName,
          })}
        >
          <div style={{ cursor: 'pointer', color: '#76b852' }}>
            <FaQrcode />
          </div>
        </OverlayPopover>
      </span>
    );
  };

  const renderOwnerCell = (location: ContainerLocation) => {
    if (CanAccessUserData(viewer)) {
      return (
        <Link
          to={Routes.users(location.ownerId)}
          style={{ wordBreak: 'break-word' }}
        >
          {location.ownerName}
        </Link>
      );
    }

    return <>{location.ownerName}</>;
  };

  const renderNextOwnerCell = (location: ContainerLocation) => {
    return (
      <>
        {!location.nextOwnerId && '-'}
        {location.nextOwnerId && (
          <Link
            to={Routes.users(location.nextOwnerId)}
            style={{ wordBreak: 'break-word' }}
          >
            {location.nextOwnerId}
          </Link>
        )}
      </>
    );
  };

  const renderContainerTypeCell = (location: ContainerLocation) => {
    return containerTypesById[location.containerTypeId] as string;
  };

  const renderContainerLocationStatusCell = (location: ContainerLocation) => {
    return ContainerLocationStatus[location.status];
  };

  const renderOwnerSinceCell = (location: ContainerLocation) => {
    return new Date(location.ownerSince).toLocaleString('de-DE');
  };

  const renderOwnerTillCell = (location: ContainerLocation) => {
    return location.ownerTill != null
      ? new Date(location.ownerTill).toLocaleString('de-DE')
      : '';
  };

  const renderContainerTransferDialog = () => {
    if (!showTransferDialog) {
      return null;
    }

    if (isSubmitting) {
      return (
        <OverlayPanel dismissable={false}>
          {i18n.t('containerSearch:pleaseWait')}
        </OverlayPanel>
      );
    }

    const hide = () => setShowTransferDialog(false);
    const execute = () => {
      if (
        window.confirm(
          i18n.t('containerSearch:transferContainersToUserConfirmation', {
            count: selection.length,
            userId: transferTargetUserId,
          }),
        )
      ) {
        Communicator.PostData('api/3/Container/BulkTransfer', {
          codes: selection
            .map((_) => `c${_.containerId}`)
            .concat([`u${transferTargetUserId}`]),
        })
          .then((response) => response.json())
          .then((responseAny) => {
            let response = responseAny as ResultResponse | null;
            if (response != null && response.result != 'Success') {
              alert(
                i18n.t('containerSearch:serverErrorMessage', {
                  error: JSON.stringify(
                    response && response.result ? response.result : response,
                  ),
                }),
              );
            }
            setIsSubmitting(false);
            setIsLoading(true);
            updateData();
          })
          .catch((response) => {
            alert(
              i18n.t('containerSearch:serverErrorMessage', {
                error: JSON.stringify(
                  response && response.result ? response.result : response,
                ),
              }),
            );
            setIsSubmitting(false);
            setIsLoading(true);
            updateData();
          });
      }
      hide();
    };

    const footer = (
      <div>
        <Button
          onClick={execute}
          disabled={
            transferTargetUserId == null || transferTargetUserId.length == 0
          }
          color="danger"
        >
          {i18n.t('containerSearch:transferContainersCount', {
            count: selection.length,
          })}
        </Button>
        <Button onClick={hide} color="secondary">
          {i18n.t('Common:cancel')}
        </Button>
      </div>
    );

    return (
      <Dialog
        header={i18n.t('containerSearch:transferContainersToUser')}
        visible={showTransferDialog}
        onHide={hide}
        style={{ minWidth: '50vw' }}
        footer={footer}
      >
        <Form>
          <FormGroup>
            <Label className="me-2">
              {i18n.t(
                'containerSearch:transferAllSelectedContainersToUserId',
              )}{' '}
            </Label>
            <Input
              id="transferTargetUserId"
              name="transferTargetUserId"
              placeholder="XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX"
              value={transferTargetUserId || ''}
              onChange={(e: any) =>
                setTransferTargetUserId(e.target.value as string | null)
              }
            />
          </FormGroup>
        </Form>
      </Dialog>
    );
  };

  const renderContainerTransferToStoreDialog = () => {
    if (!showTransferToStoreDialog) {
      return null;
    }

    if (isSubmitting) {
      return (
        <OverlayPanel dismissable={false}>
          {i18n.t('containerSearch:pleaseWait')}
        </OverlayPanel>
      );
    }

    const hide = () => setShowTransferToStoreDialog(false);
    const execute = () => {
      if (
        transferToStoreSelectedStore != null &&
        window.confirm(
          i18n.t('containerSearch:transferContainersToStoreConfirmation', {
            count: selection.length,
            storeName: transferToStoreSelectedStore.name,
          }),
        )
      ) {
        Communicator.PostData('api/3/Container/TransferToStore', {
          containerCodes: selection.map((_) => `c${_.containerId}`),
          storeId: transferToStoreSelectedStore.uuid,
        })
          .then((response) => response.json())
          .then((responseAny) => {
            let response = responseAny as ResultResponse | null;
            if (response != null && response.result != 'Success') {
              LayoutStoreInstance.setError(
                i18n.t('containerSearch:serverErrorMessage', {
                  error: JSON.stringify(
                    response && response.result ? response.result : response,
                  ),
                }),
              );
            }
            setIsSubmitting(false);
            setIsLoading(true);
            updateData();
          })
          .catch((response) =>
            response.json().then((response: any) => {
              LayoutStoreInstance.setError(
                i18n.t('containerSearch:serverErrorMessage', {
                  error: JSON.stringify(
                    response && response.result ? response.result : response,
                  ),
                }),
              );
              setIsSubmitting(false);
            }),
          );
      }
      hide();
    };

    const footer = (
      <div>
        <Button
          onClick={execute}
          disabled={transferToStoreSelectedStore == null}
          color="danger"
        >
          {i18n.t('containerSearch:transferContainersCount', {
            count: selection.length,
          })}
        </Button>
        <Button onClick={hide} color="secondary">
          {i18n.t('Common:cancel')}
        </Button>
      </div>
    );

    return (
      <Dialog
        header={i18n.t('containerSearch:transferContainersToStore')}
        visible={showTransferToStoreDialog}
        onHide={hide}
        style={{ minWidth: '50vw' }}
        footer={footer}
      >
        <Form>
          <FormGroup>
            <StoreSearch
              setStoreInfo={(
                storeUuid: string,
                storeId: string,
                name: string,
              ) => setTransferToStoreSelectedStore({ uuid: storeUuid, name })}
            />
          </FormGroup>
        </Form>
      </Dialog>
    );
  };

  const renderChangeContainerTypeDialog = () => {
    if (!showChangeTypeDialog) {
      return null;
    }

    if (isSubmitting) {
      return (
        <OverlayPanel dismissable={false}>
          {i18n.t('containerSearch:pleaseWait')}
        </OverlayPanel>
      );
    }

    const hide = () => setShowChangeTypeDialog(false);
    const execute = () => {
      if (changeTypeToTypeId === 0 || changeTypeToTypeId === null) {
        window.confirm(i18n.t('containerSearch:pleaseSelectContainerType'));
        return;
      }

      if (
        window.confirm(
          i18n.t('containerSearch:changeContainerTypeConfirmation', {
            count: selection.length,
            typeName: containerTypesById[changeTypeToTypeId],
          }),
        )
      ) {
        Communicator.PostData('container/ChangeContainerType', {
          ContainerIds: selection.map((c) => c.containerId),
          ToTypeId: Number(changeTypeToTypeId),
        })
          .then((response) => {
            setIsSubmitting(false);
            setIsLoading(true);
            updateData();
          })
          .catch((response) => {
            alert(i18n.t('containerSearch:serverErrorGeneric'));
            setIsSubmitting(false);
            setIsLoading(true);
            updateData();
          });
      }
      hide();
    };

    const footer = (
      <div>
        <Button onClick={execute} color="danger">
          {i18n.t('containerSearch:changeContainersCount', {
            count: selection.length,
          })}
        </Button>
        <Button onClick={hide} color="secondary">
          {i18n.t('Common:cancel')}
        </Button>
      </div>
    );

    return (
      <Dialog
        header={i18n.t('containerSearch:changeContainerTypes')}
        visible={showChangeTypeDialog}
        onHide={hide}
        style={{ minWidth: '50vw' }}
        footer={footer}
      >
        <Form>
          <FormGroup>
            <Label className="me-2">
              {i18n.t('containerSearch:changeTypeOfAllSelectedContainers')}
            </Label>
            <ContainerTypeSelector
              data={containerTypes}
              value={changeTypeToTypeId.toString()}
              onChange={(value: string | string[] | null) => {
                setChangeTypeToTypeId(Number(value));
              }}
              searchable
              clearable
              required
            />
          </FormGroup>
        </Form>
      </Dialog>
    );
  };

  const renderDeadlineExtendDialog = () => {
    if (!showDeadlineExtendDialog) {
      return null;
    }

    if (isSubmitting) {
      return (
        <OverlayPanel dismissable={false}>
          {i18n.t('containerSearch:pleaseWait')}
        </OverlayPanel>
      );
    }

    const hide = () => setShowDeadlineExtendDialog(false);
    const execute = () => {
      if (!newMinDeadline || new Date(newMinDeadline) < new Date(2020, 1, 1)) {
        alert(i18n.t('containerSearch:pleaseSelectValidDate'));
        return;
      }

      Communicator.PostData('container/ExtendContainerDeadline', {
        ContainerLocationIds: selection.map((c) => c.id),
        NewMinimumDeadline: newMinDeadline,
      })
        .then((response) => {
          setIsSubmitting(false);
          setIsLoading(true);
          updateData();
        })
        .catch((response) => {
          alert(i18n.t('containerSearch:serverErrorGeneric'));
          setIsSubmitting(false);
          setIsLoading(true);
          updateData();
        });
      hide();
    };

    const footer = (
      <div>
        <Button onClick={execute} color="danger">
          {i18n.t('containerSearch:extendDeadlinesCount', {
            count: selection.length,
          })}
        </Button>
        <Button onClick={hide} color="secondary">
          {i18n.t('Common:cancel')}
        </Button>
      </div>
    );

    return (
      <Dialog
        header={i18n.t('containerSearch:extendDeadlines')}
        visible={showDeadlineExtendDialog}
        onHide={hide}
        style={{ minWidth: '50vw' }}
        footer={footer}
      >
        <Form>
          <FormGroup>
            <Label className="me-2">
              {i18n.t('containerSearch:extendDeadlineOfAllSelectedContainers')}
            </Label>
            <input
              type="date"
              className="form-control"
              data-isdate
              name="newMinDeadline"
              onChange={(e) =>
                setNewMinDeadline(e.target.valueAsDate?.toISOString())
              }
              defaultValue={
                (newMinDeadline &&
                  DateTimeHelpers.toDateInputString(newMinDeadline)) ||
                new Date().toDateString()
              }
            />
            <small id="newMinDeadlineHelp" className="d-block text-muted">
              {i18n.t('containerSearch:deadlineExtensionHelp')}
            </small>
          </FormGroup>
        </Form>
      </Dialog>
    );
  };

  const renderChangeContainerLocationStatusDialog = () => {
    if (!showChangeContainerLocationStatusDialog) {
      return null;
    }

    if (isSubmitting) {
      return (
        <OverlayPanel dismissable={false}>
          {i18n.t('containerSearch:pleaseWait')}
        </OverlayPanel>
      );
    }

    const hide = () => setShowChangeContainerLocationStatusDialog(false);
    const execute = () => {
      Communicator.PostData('container/ChangeContainerLocationStatus', {
        ContainerLocationIds: selection.map((c) => c.id),
        Status: changeStatusToStatusId,
      })
        .then((response) => {
          setIsSubmitting(false);
          setIsLoading(true);
          updateData();
        })
        .catch((response) => {
          alert(i18n.t('containerSearch:serverErrorGeneric'));
          setIsSubmitting(false);
          setIsLoading(true);
          updateData();
        });
      hide();
    };

    const footer = (
      <div>
        <Button onClick={execute} color="danger">
          {i18n.t('containerSearch:changeLocationStatusCount', {
            count: selection.length,
            statusName: ContainerLocationStatus[changeStatusToStatusId],
          })}
        </Button>
        <Button onClick={hide} color="secondary">
          {i18n.t('Common:cancel')}
        </Button>
      </div>
    );

    return (
      <Dialog
        header={i18n.t('containerSearch:changeContainerLocationStatus')}
        visible={showChangeContainerLocationStatusDialog}
        onHide={hide}
        style={{ minWidth: '50vw' }}
        footer={footer}
      >
        <Form>
          <FormGroup>
            <Label className="me-2">
              {i18n.t(
                'containerSearch:changeStatusOfAllSelectedContainerLocations',
              )}
            </Label>
            <Select
              id="changeTypeToTypeId"
              name="changeTypeToTypeId"
              style={{ WebkitAppearance: 'listbox' }}
              defaultValue={changeStatusToStatusId}
              onChange={(e: any) =>
                setChangeStatusToStatusId(parseInt(e.target.value, 10))
              }
            >
              {getContainerLocationStatusOptions('update')}
            </Select>
          </FormGroup>
        </Form>
      </Dialog>
    );
  };

  const getContainerLocationStatusOptions = (type: 'filter' | 'update') => {
    const allowedStatuses = [
      ContainerLocationStatus.Lost,
      ContainerLocationStatus.LostByStore,
      ContainerLocationStatus.Broken,
      ContainerLocationStatus.BrokenAtUser,
    ];
    if (getEnvironment() !== Environment.Production || type === 'filter') {
      allowedStatuses.push(
        ContainerLocationStatus.CheckedOut,
        ContainerLocationStatus.Completed,
        ContainerLocationStatus.InStock,
        ContainerLocationStatus.OnHold,
        ContainerLocationStatus.Overdue,
        ContainerLocationStatus.OverdueError,
        ContainerLocationStatus.OverdueNoCharge,
        ContainerLocationStatus.OverdueSold,
        ContainerLocationStatus.Recovered,
      );
    }
    let options: React.ReactNode[] = [];
    const keys = Object.keys(ContainerLocationStatus)
      .map((key) => parseInt(key))
      .filter((key) => key >= 0 && allowedStatuses.includes(key));
    for (const key of keys) {
      options.push(<option value={key}>{ContainerLocationStatus[key]}</option>);
    }
    return options;
  };

  return (
    <div>
      <h3>{i18n.t('Containers:goToContainer')}</h3>
      <InputGroup>
        <Input
          placeholder={i18n.t('containerSearch:containerSearchPlaceholder')}
          value={goToContainerByIdOrUrl}
          onChange={(e) => setGoToContainerByIdOrUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              goToContainer();
              return false;
            }
            return true;
          }}
        />
        <Button
          color="secondary"
          disabled={goToContainerByIdOrUrl.length === 0}
          onClick={goToContainer}
        >
          {i18n.t('Containers:goToContainer')}
        </Button>
      </InputGroup>
      <hr />

      <Row>
        <Col md="6">
          <h3>{i18n.t('containerSearch:searchContainers')}</h3>
          <Form
            onSubmit={(e) => {
              updateData();
              return false;
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                updateData();
                return false;
              }
              return true;
            }}
          >
            {/* This disabled submit button disables any implicit form submit */}
            <button
              type="submit"
              disabled
              style={{ display: 'none' }}
              aria-hidden="true"
            />
            <Card className="mb-2">
              <CardHeader className="ps-4 pt-1 pb-0">
                <Input
                  type="checkbox"
                  name="searchOnlyCurrentLocation"
                  id="searchOnlyCurrentLocation"
                  checked={searchOnlyCurrentLocation}
                  onChange={(e) =>
                    setSearchOnlyCurrentLocation(e.target.checked)
                  }
                  style={{ WebkitAppearance: 'checkbox' }}
                />{' '}
                <Label for="searchOnlyCurrentLocation">
                  {i18n.t('containerSearch:onlyShowCurrentLocations')}
                </Label>
              </CardHeader>
            </Card>

            <Card className="mb-2">
              <CardHeader className="ps-4 pt-1 pb-0">
                <Input
                  type="checkbox"
                  name="searchContainerNameEnabled"
                  id="searchContainerNameEnabled"
                  checked={searchContainerNameEnabled}
                  onChange={(e) =>
                    setSearchContainerNameEnabled(e.target.checked)
                  }
                  style={{ WebkitAppearance: 'checkbox' }}
                />{' '}
                <Label for="searchContainerNameEnabled">
                  {i18n.t('containerSearch:onlyShowContainersWithSpecificName')}
                </Label>
              </CardHeader>
              {searchContainerNameEnabled ? (
                <CardBody>
                  <Input
                    id="searchContainerName"
                    name="searchContainerName"
                    type="text"
                    placeholder={i18n.t('containerSearch:containerSearchQuery')}
                    disabled={!searchContainerNameEnabled}
                    value={searchContainerName}
                    onChange={(e) => setSearchContainerName(e.target.value)}
                    style={{ marginBottom: '0.5rem' }}
                  />{' '}
                  <Input
                    type="checkbox"
                    name="searchContainerNameExactMatch"
                    id="searchContainerNameExactMatch"
                    disabled={!searchContainerNameEnabled}
                    checked={searchContainerNameExactMatch}
                    onChange={(e) =>
                      setSearchContainerNameExactMatch(e.target.checked)
                    }
                    style={{ WebkitAppearance: 'checkbox', marginLeft: '0' }}
                  />
                  <Label
                    for="searchContainerNameExactMatch"
                    style={{ marginLeft: '1.25rem' }}
                  >
                    {i18n.t('containerSearch:requireExactMatch')}
                  </Label>
                </CardBody>
              ) : null}
            </Card>

            <Card className="mb-2">
              <CardHeader className="ps-4 pt-1 pb-0">
                <Input
                  type="checkbox"
                  name="searchContainerTypeIdEnabled"
                  id="searchContainerTypeIdEnabled"
                  checked={searchContainerTypeIdEnabled}
                  onChange={(e) =>
                    setSearchContainerTypeIdEnabled(e.target.checked)
                  }
                  style={{ WebkitAppearance: 'checkbox' }}
                />{' '}
                <Label for="searchContainerTypeIdEnabled">
                  {i18n.t('containerSearch:onlyShowContainersOfSpecificType')}
                </Label>
              </CardHeader>
              {searchContainerTypeIdEnabled ? (
                <CardBody>
                  <ContainerTypeSelector
                    data={containerTypes}
                    value={searchContainerTypeId.toString()}
                    onChange={(value) =>
                      setSearchContainerTypeId(Number(value))
                    }
                    searchable
                    clearable
                  />
                </CardBody>
              ) : null}
            </Card>

            <Card className="mb-2">
              <CardHeader className="ps-4 pt-1 pb-0">
                <Input
                  type="checkbox"
                  name="searchContainerOwnerIdEnabled"
                  id="searchContainerOwnerIdEnabled"
                  checked={searchContainerOwnerIdEnabled}
                  onChange={(e) =>
                    setSearchContainerOwnerIdEnabled(e.target.checked)
                  }
                  style={{ WebkitAppearance: 'checkbox' }}
                />{' '}
                <Label for="searchContainerOwnerIdEnabled">
                  {i18n.t(
                    'containerSearch:onlyShowContainersBookedToSpecificUserId',
                  )}
                </Label>
              </CardHeader>
              {searchContainerOwnerIdEnabled ? (
                <CardBody>
                  <Input
                    id="searchContainerOwnerId"
                    name="searchContainerOwnerId"
                    type="text"
                    placeholder="XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX"
                    disabled={!searchContainerOwnerIdEnabled}
                    value={searchContainerOwnerId}
                    onChange={(e) => setSearchContainerOwnerId(e.target.value)}
                  />
                </CardBody>
              ) : null}
            </Card>
            <Card className="mb-2">
              <CardHeader className="ps-4 pt-1 pb-0">
                <Input
                  type="checkbox"
                  name="searchContainerPreviousOwnerIdEnabled"
                  id="searchContainerPreviousOwnerIdEnabled"
                  checked={searchContainerPreviousOwnerIdEnabled}
                  onChange={(e) =>
                    setSearchContainerPreviousOwnerIdEnabled(e.target.checked)
                  }
                  style={{ WebkitAppearance: 'checkbox' }}
                />{' '}
                <Label for="searchContainerPreviousOwnerIdEnabled">
                  {i18n.t(
                    'containerSearch:onlyShowContainersWithSpecificPreviousOwner',
                  )}
                </Label>
              </CardHeader>
              {searchContainerPreviousOwnerIdEnabled ? (
                <CardBody>
                  <Input
                    id="searchContainerPreviousOwnerId"
                    name="searchContainerPreviousOwnerId"
                    type="text"
                    placeholder="XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX"
                    disabled={!searchContainerPreviousOwnerIdEnabled}
                    value={searchContainerPreviousOwnerId}
                    onChange={(e) =>
                      setSearchContainerPreviousOwnerId(e.target.value)
                    }
                  />
                </CardBody>
              ) : null}
            </Card>
            <Card className="mb-2">
              <CardHeader className="ps-4 pt-1 pb-0">
                <Input
                  type="checkbox"
                  name="searchContainerStoreIdEnabled"
                  id="searchContainerStoreIdEnabled"
                  checked={searchContainerStoreIdEnabled}
                  onChange={(e) =>
                    setSearchContainerStoreIdEnabled(e.target.checked)
                  }
                  style={{ WebkitAppearance: 'checkbox' }}
                />{' '}
                <Label for="searchContainerStoreIdEnabled">
                  {i18n.t(
                    'containerSearch:onlyShowContainersBookedToSpecificStore',
                  )}
                </Label>
              </CardHeader>
              {searchContainerStoreIdEnabled && (
                <CardBody>
                  <StoreSearch
                    setStoreInfo={(
                      storeUuid: string,
                      storeId: string,
                      name: string,
                    ) => {
                      setSelectedStoreId(storeUuid);
                      setSelectedStore({ uuid: storeUuid, name });
                    }}
                  />
                </CardBody>
              )}
            </Card>

            <Card className="mb-2">
              <CardHeader className="ps-4 pt-1 pb-0">
                <Input
                  type="checkbox"
                  name="searchContainerLocationStatusEnabled"
                  id="searchContainerLocationStatusEnabled"
                  checked={searchContainerLocationStatusEnabled}
                  onChange={(e) =>
                    setSearchContainerLocationStatusEnabled(e.target.checked)
                  }
                  style={{ WebkitAppearance: 'checkbox' }}
                />{' '}
                <Label for="searchContainerLocationStatusEnabled">
                  {i18n.t(
                    'containerSearch:onlyShowContainersLocationWithSpecificStatus',
                  )}
                </Label>
              </CardHeader>
              {searchContainerLocationStatusEnabled && (
                <CardBody>
                  <Select
                    id="searchContainerLocationStatus"
                    name="searchContainerLocationStatus"
                    style={{ WebkitAppearance: 'listbox' }}
                    value={searchContainerLocationStatusId}
                    onChange={(e: any) =>
                      setSearchContainerLocationStatusId(
                        parseInt(e.target.value, 10),
                      )
                    }
                  >
                    {getContainerLocationStatusOptions('filter')}
                  </Select>
                </CardBody>
              )}
            </Card>
            <Card className="mb-2">
              <CardHeader className="ps-4 pt-1 pb-0">
                <Input
                  type="checkbox"
                  name="searchTapToBorrowTransactionIdEnabled"
                  id="searchTapToBorrowTransactionIdEnabled"
                  checked={searchTapToBorrowTransactionIdEnabled}
                  onChange={(e) =>
                    setSearchTapToBorrowTransactionIdEnabled(e.target.checked)
                  }
                  style={{ WebkitAppearance: 'checkbox' }}
                />{' '}
                <Label for="searchTapToBorrowTransactionIdEnabled">
                  {i18n.t(
                    'containerSearch:onlyShowContainersWithTapToBorrowTransactionId',
                  )}
                </Label>
              </CardHeader>
              {searchTapToBorrowTransactionIdEnabled && (
                <CardBody>
                  <Input
                    id="searchTapToBorrowTransactionId"
                    name="searchTapToBorrowTransactionId"
                    type="text"
                    placeholder={i18n.t(
                      'containerSearch:tapToBorrowTransactionIdPlaceholder',
                    )}
                    disabled={!searchTapToBorrowTransactionIdEnabled}
                    value={searchTapToBorrowTransactionId}
                    onChange={(e) =>
                      setSearchTapToBorrowTransactionId(e.target.value)
                    }
                    style={{ marginBottom: '0.5rem' }}
                  />
                </CardBody>
              )}
            </Card>
            <Button onClick={() => updateData()} color="primary" block>
              {i18n.t('Common:search')}
            </Button>
          </Form>
        </Col>
        <Col md="6">
          {selection.length > 0 ? (
            <div>
              <h3>{i18n.t('containerSearch:forAllSelectedContainers')}</h3>
              <Button block onClick={() => setShowChangeTypeDialog(true)}>
                {i18n.t('containerSearch:changeTheContainerType')}
              </Button>
              <Button block onClick={() => setShowTransferToStoreDialog(true)}>
                {i18n.t('containerSearch:transferContainersToStore')}
              </Button>
              <Button block onClick={() => setShowTransferDialog(true)}>
                {i18n.t('containerSearch:transferContainersToUser')}
              </Button>
              <Button block onClick={() => setShowDeadlineExtendDialog(true)}>
                {i18n.t('containerSearch:extendContainerDeadlines')}
              </Button>
              <Button
                block
                onClick={() => setShowChangeContainerLocationStatusDialog(true)}
              >
                {i18n.t('containerSearch:changeContainerLocationStatus')}
              </Button>
            </div>
          ) : (
            <p>{i18n.t('containerSearch:selectSomeContainersToShowActions')}</p>
          )}
        </Col>
      </Row>
      <hr />
      <p>
        {i18n.t('containerSearch:showingResultsCount', {
          current: locations ? locations.length : 0,
          total: totalCount || 0,
        })}
      </p>
      <DataTable
        value={locations}
        selection={selection}
        selectionMode="multiple"
        onSelectionChange={(e) => setSelection(e.value as ContainerLocation[])}
        dataKey="id"
        lazy
        paginator
        loading={isLoading || isSubmitting}
        totalRecords={totalCount}
        rows={listLimit}
        first={listOffset}
        onPage={(e) => {
          updateData(e.first);
        }}
        className="p-datatable-striped"
        style={{ width: '100%', overflowX: 'scroll' }}
      >
        <Column headerStyle={{ width: '70px' }} selectionMode="multiple" />
        <Column
          headerStyle={{ width: '90px' }}
          field="id"
          header={i18n.t('containerSearch:columnHeaderId')}
        />
        <Column
          headerStyle={{
            width: searchOnlyCurrentLocation ? '200px' : '100px',
          }}
          body={(l: ContainerLocation) => renderOwnerCell(l)}
          header={i18n.t('containerSearch:columnHeaderLocation')}
        />
        {!searchOnlyCurrentLocation && (
          <Column
            headerStyle={{ width: '100px' }}
            body={(l: ContainerLocation) => renderNextOwnerCell(l)}
            header={i18n.t('containerSearch:columnHeaderNextLocation')}
          />
        )}
        <Column
          headerStyle={{
            width: searchOnlyCurrentLocation ? '150px' : '100px',
          }}
          body={(l: ContainerLocation) => renderOwnerSinceCell(l)}
          header={i18n.t('containerSearch:columnHeaderTransactionDate')}
        />
        {!searchOnlyCurrentLocation && (
          <Column
            headerStyle={{ width: '100px' }}
            body={(l: ContainerLocation) => renderOwnerTillCell(l)}
            header={i18n.t('containerSearch:columnHeaderNextTransactionDate')}
          />
        )}
        <Column
          headerStyle={{ width: '150px' }}
          body={(l: ContainerLocation) => renderContainerCell(l)}
          header={i18n.t('containerSearch:columnHeaderContainer')}
        />
        <Column
          headerStyle={{ width: '150px' }}
          body={(l: ContainerLocation) => renderContainerTypeCell(l)}
          header={i18n.t('containerSearch:columnHeaderContainerType')}
        />
        <Column
          headerStyle={{ width: '150px' }}
          body={(l: ContainerLocation) => renderContainerLocationStatusCell(l)}
          header={i18n.t('containerSearch:columnHeaderStatus')}
        />
      </DataTable>
      {renderChangeContainerTypeDialog()}
      {renderContainerTransferDialog()}
      {renderContainerTransferToStoreDialog()}
      {renderDeadlineExtendDialog()}
      {renderChangeContainerLocationStatusDialog()}
    </div>
  );
};

export default ContainerListView;
